import { useEffect, useState } from 'react';
import { ArrowLeft, Factory, Save } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '@/Components/ui/Button';
import { Card } from '@/Components/ui/Card';
import { Checkbox } from '@/Components/ui/Checkbox';
import { Input } from '@/Components/ui/Input';
import { PageHeader } from '@/Components/data/PageHeader';
import DecimalInput from '@/Components/forms/DecimalInput';
import { Field } from '@/Components/forms/Field';
import FormStickyFooter from '@/Components/forms/FormStickyFooter';
import centroTrabalhoService, { type CentroTrabalhoInput } from '@/services/centroTrabalho';

const FORM_VAZIO: CentroTrabalhoInput = {
  codigo: '',
  descricao: '',
  tipo: '',
  capacidade_hora: null,
  custo_hora: null,
  ativo: true,
};

export default function CentroTrabalhoForm() {
  const navigate = useNavigate();
  const { id } = useParams();
  const editando = Boolean(id);
  const [form, setForm] = useState<CentroTrabalhoInput>(FORM_VAZIO);
  const [loading, setLoading] = useState(editando);
  const [salvando, setSalvando] = useState(false);

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    centroTrabalhoService.buscarPorId(id)
      .then((data) => {
        setForm({
          codigo: data.codigo,
          descricao: data.descricao,
          tipo: data.tipo,
          capacidade_hora: data.capacidade_hora ?? null,
          custo_hora: data.custo_hora ?? null,
          ativo: data.ativo,
        });
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [id]);

  const salvar = async () => {
    if (!form.descricao || !form.tipo) return;
    setSalvando(true);
    try {
      if (id) {
        await centroTrabalhoService.atualizar(id, form);
      } else {
        await centroTrabalhoService.criar(form);
      }
      navigate('/producao/centro-trabalho');
    } catch (error) {
      console.error('Erro ao salvar centro de trabalho', error);
    } finally {
      setSalvando(false);
    }
  };

  return (
    <form className="nx-page nx-sngpc-form-page" onSubmit={(event) => { event.preventDefault(); void salvar(); }}>
      <PageHeader
        icon={<Factory size={21} strokeWidth={2} />}
        title={editando ? 'Editar Centro de Trabalho' : 'Novo Centro de Trabalho'}
        description={editando ? 'Atualize os dados cadastrais do centro de trabalho.' : 'Preencha os dados para incluir um novo centro de trabalho.'}
        actions={<Button type="button" variant="ghost" onClick={() => navigate('/producao/centro-trabalho')}><ArrowLeft size={16} /> Voltar</Button>}
      />

      <Card className="nx-page-card nx-sngpc-form-card">
        <section className="nx-sngpc-form-section">
          <div className="nx-sngpc-form-section__header">
            <h2>Identificação</h2>
          </div>
          <div className="nx-sngpc-page-form-grid">
            <Field label="Código" htmlFor="centro-codigo">
              <Input id="centro-codigo" value={editando ? form.codigo || '' : 'Gerado ao salvar'} readOnly disabled={loading} />
            </Field>
            <Field label="Tipo *" htmlFor="centro-tipo">
              <Input id="centro-tipo" value={form.tipo || ''} onChange={(e) => setForm({ ...form, tipo: e.target.value })} disabled={loading} required />
            </Field>
            <Field label="Descrição *" htmlFor="centro-descricao" className="nx-sngpc-field--full">
              <Input id="centro-descricao" value={form.descricao || ''} onChange={(e) => setForm({ ...form, descricao: e.target.value })} disabled={loading} required />
            </Field>
          </div>
        </section>

        <section className="nx-sngpc-form-section">
          <div className="nx-sngpc-form-section__header">
            <h2>Capacidade e custos</h2>
          </div>
          <div className="nx-sngpc-page-form-grid">
            <Field label="Capacidade (h)" htmlFor="centro-capacidade">
              <DecimalInput id="centro-capacidade" value={form.capacidade_hora ?? null} onValueChange={(value) => setForm({ ...form, capacidade_hora: value })} disabled={loading} />
            </Field>
            <Field label="Custo por Hora (R$)" htmlFor="centro-custo">
              <DecimalInput id="centro-custo" value={form.custo_hora ?? null} onValueChange={(value) => setForm({ ...form, custo_hora: value })} disabled={loading} />
            </Field>
            <label className="nx-sngpc-checkbox-field">
              <Checkbox checked={form.ativo ?? true} onChange={(e) => setForm({ ...form, ativo: e.target.checked })} disabled={loading} />
              <span>Ativo</span>
            </label>
          </div>
        </section>
      </Card>

      <FormStickyFooter
        cancelLabel="Cancelar"
        onCancel={() => navigate('/producao/centro-trabalho')}
        saveLabel="Salvar"
        loading={salvando}
        saveIcon={<Save size={16} />}
      />
    </form>
  );
}
